<template>
  <div class="page-content">
    <div class="payment">
      <div class="payment-header">
        <CustomIcon name="logo" class="payment-header-logo" />
        <h2 class="payment-title">Get started</h2>
      </div>
      <h3 class="payment-subtitle">Set Up Your Account</h3>
      <FieldInput
        name="name"
        placeholder="First Name"
        :value="formData.name.value"
        :touched="formData.name.touched"
        :onBlur="() => updateForm('name', formData.name.value, true)"
        :onChange="(event) => updateForm('name', event.target.value, true)"
        :validator="(value) => validator['name'](value)"
      />
      <FieldInput
        name="email"
        placeholder="Email"
        :value="formData.email.value"
        :touched="formData.email.touched"
        :onBlur="() => updateForm('email', formData.email.value, true)"
        :onChange="(event) => updateForm('email', event.target.value, true)"
        :validator="(value) => validator['email'](value)"
      />
      <p class="payment-text">
        oVRcome will use this email to authenticate log-ins, ensure you have
        access to it on your mobile phone.
      </p>
      <SnackbarPrompt v-if="errorMessage" icon="warning" variation="warning">{{
        errorMessage
      }}</SnackbarPrompt>
      <CustomButton
        :disabled="!formIsValid() || registerUserIsLoading"
        :onClick="(e) => handleRegisterUser(e)"
      >
        <span v-if="!registerUserIsLoading">Continue</span>
        <LoadingIndicator v-else />
      </CustomButton>
    </div>
  </div>
</template>
<script>
import { EXTERNAL_ROUTES, PAGE_PATH, MODULES, PLAN_TYPES } from "@/config";
import { webPaymentSetupAccountValidator } from "@/utils/form-validation/index";
import { getPlansParams } from "@/utils/get-plans-params";
import { pageInformationMixin } from "@/mixins/index.js";
import { denormaliseOrderSummary, getCourseType } from "@/utils";
import CustomIcon from "@/components/CustomIcon.vue";
import FieldInput from "@/components/FieldInput.vue";
import CustomButton from "@/components/CustomButton.vue";
import LoadingIndicator from "@/components/LoadingIndicator.vue";
import SnackbarPrompt from "@/components/SnackbarPrompt.vue";
import moment from "moment";
import { mapGetters } from "vuex";

export default {
  data() {
    return {
      validator: webPaymentSetupAccountValidator,
      terms: EXTERNAL_ROUTES.termsAndConditions,
      privacyPolicy: EXTERNAL_ROUTES.privacyPolicy,
      registerUserIsLoading: false,
      errorMessage: null,
    };
  },
  props: {
    updateFormState: Function,
    action: Function,
    formData: Object,
  },
  components: {
    CustomIcon,
    FieldInput,
    CustomButton,
    LoadingIndicator,
    SnackbarPrompt,
  },
  mixins: [pageInformationMixin],
  computed: {
    ...mapGetters(MODULES.WELCOME, {
      onboardingData: "getOnboardingData",
    }),
    ...mapGetters(MODULES.PLAN, {
      availablePlans: "getAvailablePlans",
    }),
    ...mapGetters(MODULES.ANXIETY, {
      anxietyData: "getAnxietyData",
    }),
  },
  mounted() {
    const { dispatch } = this.$store;
    const { formData, onboardingData } = this;

    dispatch(
      `${MODULES.PLAN}/getPlansAction`,
      getPlansParams({ onboardingData })
    ).then(() => {
      if (!formData.plan_id.value) {
        this.updateFormState("plan_id", this.getPlans()[0]?.id, false);
      }
    });
    this.trackEmail();
  },
  methods: {
    updateForm(key, value, touched = false) {
      const { updateFormState } = this;

      updateFormState(key, value, touched);
    },
    formIsValid() {
      let keys = Object.keys(this.formData);

      const properties = (validator, key) =>
        Object.prototype.hasOwnProperty.call(validator, key);

      const formIsValid = !keys.some((key) => {
        if (properties(this.validator, key)) {
          return !this.validator[key](this.formData[key].value).valid;
        }

        return false;
      });

      return formIsValid;
    },
    getPlans() {
      const { onboardingData } = this;

      const plans = this.availablePlans;

      if (onboardingData.choose_path === PAGE_PATH.myself) {
        return plans.filter((plan) => plan.type === PLAN_TYPES.globalPlan);
      }

      if (onboardingData.choose_path === PAGE_PATH.parent) {
        return this.availablePlans;
      }
    },
    handleRegisterUser(e) {
      if (e) {
        e.preventDefault();
      }

      const { dispatch } = this.$store;
      const { choose_path, age_select, choose_phobias, choose_avatar } =
        this.onboardingData;

      this.registerUserIsLoading = true;

      if (choose_path === PAGE_PATH.parent) {
        dispatch(`${MODULES.USER}/createEvent`, {
          events: [
            {
              category_id: age_select,
              phobias: choose_phobias,
              type: "user_programme_phobias_selected",
              created_at: moment().format("YYYY/MM/DD HH:mm:ss Z"),
            },
            {
              avatar: choose_avatar?.toString(),
              type: "user_attribute_provided",
              created_at: moment().format("YYYY/MM/DD HH:mm:ss Z"),
            },
          ],
        })
          .then(() => this.registerUser())
          .catch(() => {
            this.registerUserIsLoading = false;
            this.errorMessage =
              "Something went wrong trying to set up your account. Please contact ovrcome support: support@ovrcome.io";
          });
      } else {
        this.registerUser();
      }
    },
    registerUser() {
      const { dispatch } = this.$store;

      const {
        age_select,
        choose_gender,
        choose_path,
        choose_phobias,
        name_input,
        choose_ethnicity,
        choose_treatments,
        reason_select,
      } = this.onboardingData;

      const { choose_comforts, choose_fears, choose_feeling } =
        this.anxietyData;

      let payload = {
        email: this.formData.email.value,
        first_name: this.formData.name.value,
      };

      if (choose_path === PAGE_PATH.myself) {
        payload = {
          ...payload,
          category_id: choose_phobias,
          course_type: "full",
          gender: choose_gender,
          paymentType: "premium",
          details: {
            gender: choose_gender,
            age: age_select,
            ethnicity: choose_ethnicity,
            onboarding_tried_treatments: choose_treatments,
            onboarding_reason_for_doing_program: reason_select,
            onboarding_i_want_to_be_comfortable_when: choose_comforts,
            onboarding_what_scares_me: choose_fears,
            onboarding_how_anxious: choose_feeling,
          },
        };
      } else if (choose_path === PAGE_PATH.parent) {
        payload = {
          ...payload,
          category_id: age_select,
          course_type: "childrens",
          first_name: name_input,
          gender: choose_gender,
          paymentType: "parent",
          source: "parent_via_app",
        };
      }

      dispatch(`${MODULES.USER}/registerUser`, payload)
        .then((responseData) => {
          if (responseData?.programmes?.some((prog) => prog.plan_id)) {
            const error = "Error: You already have an account setup.";
            this.trackUserError({ message: error });

            return;
          }

          dispatch(`${MODULES.PAYMENT}/getSaleSummaryAction`, {
            email: payload.email,
            paymentType: payload.paymentType,
            ...denormaliseOrderSummary(this.formData),
            voucher_code: this.formData.promoApplied.value,
          })
            .then(() => this.identifyNewUserEvent(responseData))
            .then(() =>
              this.action(
                this.formData.name.value,
                "name_input",
                this.formData.email.value,
                "email_input"
              )
            )
            .then(() => (this.registerUserIsLoading = false));
        })
        .catch((e) => {
          this.registerUserIsLoading = false;
          this.trackUserError(e);
        });
    },
    trackEmail() {
      if (window?.analytics) {
        window.analytics.track("New user - Ask for email", {
          courseType: getCourseType(this.onboardingData.choose_path),
          Host: "Web",
        });
      }
    },
    trackUserError(error) {
      this.errorMessage = error?.message || error?.error;
      this.registerUserIsLoading = false;

      if (window?.analytics && window?.analytics?.track) {
        window.analytics.track("New user - Has an account already", {
          Host: "Web",
          type: error?.message || error?.error,
        });
      }
    },
    identifyNewUserEvent(responseData) {
      if (
        responseData?.id &&
        window?.analytics &&
        window?.analytics?.identify &&
        window?.analytics?.track
      ) {
        const { plan_id } = this.formData;
        const { email, id } = responseData;

        const planInfo = this.getPlans().find(
          (plan) => plan_id.value === plan.id
        );

        window.analytics.identify(
          id,
          {
            email,
            subscriptionPeriod: planInfo?.billing_units,
            currency: planInfo?.first_month_price.name,
          },
          {},
          () => {
            window.analytics.track("New User - Account Created", {
              Host: "Web",
              subscriptionPeriod: planInfo?.billing_units,
              currency: planInfo?.first_month_price.name,
              planValue: planInfo?.first_month_price.amount,
            });

            this.updateFormState("user_id", id, false);
          }
        );
      }
    },
  },
};
</script>
